/* =====================================================================
   AstaLab 2.0 — frontend/src/utils/clientLogger.js (round Beta-P3-5, design D2/D3, accettato)

   Unico, minuscolo helper diagnostico lato client — SOLO console.error, MAI rete, persistenza,
   livelli, transport/sink pluggable, configurazione. Copre esattamente gli 11 punti di
   instrumentazione selezionati (auth listener, rollback legacy, live poll, 8 catch difensivi di
   retry) — mai un logging generico applicato a ogni console.* esistente.

   Confine di fiducia (round D3, correzione di un difetto del design D2): `error` è un valore
   COMPLETAMENTE non fidato — non viene MAI letta alcuna sua proprietà (name/code/status/message/
   stack), MAI serializzato/stringificato, MAI stampato un valore stringa grezzo lanciato
   direttamente. L'UNICA informazione estratta è la sua classificazione STRUTTURALE (instanceof/
   typeof) — un insieme chiuso e fisso di etichette possedute dal logger stesso, mai una stringa
   copiata dall'input. Questo è ciò che rende impossibile, per costruzione, che un oggetto
   malevolo/inatteso come `throw {name:"password=...", code:"token-..."}` faccia trapelare quei
   valori: non è mai `instanceof Error`, quindi viene classificato semplicemente "object" senza mai
   leggere `.name`/`.code`.

   `area`/`operation` sono SEMPRE literal tecnici hardcoded ai punti di chiamata selezionati — mai
   derivati da route/input utente/contenuto dell'errore/messaggi backend (round D2 §6).

   Contesto: allowlist di sole chiavi tecniche scalari (round D2 §4/§5, invariata) — mai un
   denylist generico, mai valori annidati/oggetti/array/funzioni, mai una chiave sconosciuta.

   Resilienza (round D3 §3/§5, correzioni esplicite): la classificazione strutturale tollera un
   Proxy ostile il cui `instanceof` stesso lancia (mai un aborto della classificazione); l'ispezione
   del contesto è best-effort per-chiave (una singola chiave che lancia in lettura viene saltata,
   mai propagata) e per l'intero oggetto (un Proxy ostile passato come `context` produce
   semplicemente `safeContext = {}`, mai un blocco del log stesso); l'intera funzione è avvolta in
   un try/catch esterno — anche un fallimento imprevisto di `console.error` stesso viene assorbito,
   mai propagato nel flusso applicativo, mai un secondo tentativo di log del fallimento del logger. */

const ALLOWED_CONTEXT_KEYS = new Set([
  "auctionId", "nominationId", "seatId", "memberId", "playerId", "consecutiveFailures"
]);

function isSafeScalar(value){
  return value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

/** Classificazione PURAMENTE strutturale — mai la lettura di una proprietà di `error`. */
function classifyErrorKind(error){
  if(error === null) return "null";
  const basicKind = typeof error;
  if(basicKind === "object"){
    try{
      if(error instanceof Error) return "Error";
    }catch(e){
      // Proxy ostile il cui `instanceof`/getPrototypeOf lancia: resta "object", mai un aborto.
    }
  }
  return basicKind;
}

/** Best-effort, mai propagante: una chiave che lancia in lettura viene saltata; un `context`
 *  interamente ostile (es. un Proxy che lancia su Object.keys/ownKeys) produce `{}`. */
function buildSafeContext(context){
  const safeContext = {};
  try{
    if(!context || typeof context !== "object" || Array.isArray(context)) return safeContext;
    for(const key of Object.keys(context)){
      if(!ALLOWED_CONTEXT_KEYS.has(key)) continue;
      try{
        const value = context[key];
        if(isSafeScalar(value)) safeContext[key] = value;
      }catch(e){ /* accesso alla singola chiave fallito: saltata, mai propagata */ }
    }
  }catch(e){ return {}; }
  return safeContext;
}

/**
 * @param {{area:string, operation:string, error:*, context?:Object}} params
 * @returns {void}
 */
export function logClientError({ area, operation, error, context }){
  try{
    const kind = classifyErrorKind(error);
    const safeContext = buildSafeContext(context);
    console.error("[" + String(area) + ":" + String(operation) + "]", { kind }, safeContext);
  }catch(loggingFailure){
    // Il logger diagnostico non deve mai propagare un proprio fallimento nel flusso applicativo —
    // mai un secondo tentativo di loggare QUESTO fallimento.
  }
}
